import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, Star, Heart, Filter, ChevronDown, ChevronUp, Search, Coffee, Music, Home, Users2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';

const culturalTours = [
  {
    id: 1,
    title: 'Batwa Cultural Experience',
    image: 'https://imgur.com/5v6lVl8.jpg',
    location: 'Bwindi Forest Region',
    duration: 'Full Day',
    groupSize: '2-8 people',
    price: 85,
    rating: 4.9,
    reviews: 67,
    description: 'Immerse yourself in the ancient traditions of the Batwa people, the original inhabitants of the Bwindi Forest.',
    tag: 'Indigenous Culture',
    highlights: [
      'Traditional hunting techniques',
      'Forest medicine demonstration',
      'Cultural performances',
      'Story-telling sessions'
    ]
  },
  {
    id: 2,
    title: 'Royal Buganda Kingdom Tour',
    image: 'https://imgur.com/LTxFu0W.jpg',
    location: 'Kampala',
    duration: '6 hours',
    groupSize: '2-10 people',
    price: 65,
    rating: 4.8,
    reviews: 89,
    description: 'Explore the rich heritage of the Buganda Kingdom, including visits to the Kasubi Tombs and the Royal Palace.',
    tag: 'Royal Heritage',
    highlights: [
      'Royal palace visit',
      'Traditional ceremonies',
      'Royal artifacts',
      'Cultural guides'
    ]
  },
  {
    id: 3,
    title: 'Karamoja Cultural Safari',
    image: 'https://imgur.com/eBF3SoG.jpg',
    location: 'Karamoja Region',
    duration: '2 Days',
    groupSize: '2-6 people',
    price: 195,
    rating: 4.7,
    reviews: 45,
    description: 'Experience the unique culture and traditions of the Karamojong people in northeastern Uganda.',
    tag: 'Traditional Life',
    highlights: [
      'Village visits',
      'Traditional dances',
      'Craft workshops',
      'Local cuisine'
    ]
  },
  {
    id: 4,
    title: 'Traditional Cooking Experience',
    image: 'https://imgur.com/4kwMnNu.jpg',
    location: 'Kampala',
    duration: '4 hours',
    groupSize: '2-8 people',
    price: 45,
    rating: 4.9,
    reviews: 112,
    description: 'Learn to prepare traditional Ugandan dishes with local chefs in a hands-on cooking class.',
    tag: 'Food & Culture',
    highlights: [
      'Market visit',
      'Cooking lessons',
      'Traditional recipes',
      'Shared meal'
    ]
  },
  {
    id: 5,
    title: 'Ndere Cultural Evening',
    image: 'https://imgur.com/V4EbZrd.jpg',
    location: 'Kampala',
    duration: '4 hours',
    groupSize: '2-20 people',
    price: 55,
    rating: 4.8,
    reviews: 156,
    description: 'Experience traditional music, dance, and storytelling at the renowned Ndere Cultural Centre.',
    tag: 'Performance',
    highlights: [
      'Traditional dances',
      'Live music',
      'Cultural dinner',
      'Interactive shows'
    ]
  },
  {
    id: 6,
    title: 'Craft Village Experience',
    image: 'https://imgur.com/8aMF2or.jpg',
    location: 'Multiple Locations',
    duration: '3 hours',
    groupSize: '1-6 people',
    price: 35,
    rating: 4.7,
    reviews: 78,
    description: 'Learn traditional crafts from local artisans and create your own souvenirs.',
    tag: 'Arts & Crafts',
    highlights: [
      'Craft workshops',
      'Local artisans',
      'Souvenir making',
      'Cultural insights'
    ]
  }
];

const CulturalTours = () => {
  const [selectedTour, setSelectedTour] = useState<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 200]);
  const [durationFilter, setDurationFilter] = useState<string[]>([]);
  const { t } = useTranslation();

  const filteredTours = culturalTours.filter(tour => {
    const matchesSearch = tour.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tour.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = tour.price >= priceRange[0] && tour.price <= priceRange[1];
    const matchesDuration = durationFilter.length === 0 || 
                           durationFilter.some(d => tour.duration.toLowerCase().includes(d.toLowerCase()));
    
    return matchesSearch && matchesPrice && matchesDuration;
  });

  const handleBookNow = (tour: any) => {
    setSelectedTour(tour);
    setIsBookingModalOpen(true);
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/5v6lVl8.jpg"
            alt="Cultural experience"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/uganda-trips" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common.back')}
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold mb-4">{t('adventures.culturalTours')}</h1>
            <p className="text-lg text-white/90 mb-6">
              {t('pages.customizeAdventure.subtitle')}
            </p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:ring-ta-green focus:border-ta-green"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Users2 className="h-6 w-6 text-ta-green" />
              </div>
              <div className="text-2xl font-bold text-gray-900">56+</div>
              <div className="text-sm text-gray-600">{t('common.ethnicGroups')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Music className="h-6 w-6 text-ta-green" />
              </div>
              <div className="text-2xl font-bold text-gray-900">40+</div>
              <div className="text-sm text-gray-600">{t('common.languages')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Home className="h-6 w-6 text-ta-green" />
              </div>
              <div className="text-2xl font-bold text-gray-900">5+</div>
              <div className="text-sm text-gray-600">{t('common.kingdoms')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Coffee className="h-6 w-6 text-ta-green" />
              </div>
              <div className="text-2xl font-bold text-gray-900">100+</div>
              <div className="text-sm text-gray-600">{t('common.culturalSites')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="sticky top-14 z-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              {t('pages.allExperiences.filters')}
              {isFilterOpen ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </button>
          </div>

          {isFilterOpen && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.priceRange')}</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>${priceRange[0]}</span>
                      <span>${priceRange[1]}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      step="10"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-ta-green"
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">{t('pages.safariPackages.duration')}</h3>
                  <div className="space-y-2">
                    {['2-4 hours', 'Half Day', 'Full Day', '2 Days'].map((duration) => (
                      <label key={duration} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={durationFilter.includes(duration)}
                          onChange={() => {
                            if (durationFilter.includes(duration)) {
                              setDurationFilter(durationFilter.filter(d => d !== duration));
                            } else {
                              setDurationFilter([...durationFilter, duration]);
                            }
                          }}
                          className="rounded border-gray-300 text-ta-green focus:ring-ta-green"
                        />
                        <span className="ml-2 text-sm text-gray-700">{duration}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTours.map((tour) => (
              <div 
                key={tour.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-48">
                  <img 
                    src={tour.image}
                    alt={tour.title}
                    className="w-full h-full object-cover"
                  />
                  <button className="absolute top-2 right-2 p-1.5 bg-white/80 rounded-full hover:bg-white transition-colors">
                    <Heart className="h-4 w-4 text-gray-600 hover:text-red-500" />
                  </button>
                  {tour.tag && (
                    <div className="absolute top-2 left-2 bg-ta-green text-white text-xs px-2 py-0.5 rounded-full">
                      {tour.tag}
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-center mb-2">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span className="ml-1 text-sm font-medium">{tour.rating}</span>
                    <span className="mx-1 text-gray-400 text-sm">•</span>
                    <span className="text-sm text-gray-600">{tour.reviews} {t('common.reviews')}</span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{tour.title}</h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 text-ta-green mr-1" />
                      {tour.location}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 text-ta-green mr-1" />
                      {tour.duration}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 text-ta-green mr-1" />
                      {tour.groupSize}
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{tour.description}</p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {tour.highlights.map((highlight, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-ta-green/10 text-ta-green rounded-full text-xs"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">{t('common.from')}</p>
                      <p className="text-xl font-bold text-gray-900">${tour.price}</p>
                    </div>
                    <button
                      onClick={() => handleBookNow(tour)}
                      className="px-4 py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors text-sm"
                    >
                      {t('pages.cityTours.bookNow')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {selectedTour && (
        <BookingModal
          experience={selectedTour}
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedTour(null);
          }}
        />
      )}

      <Footer />
    </>
  );
};

export default CulturalTours;